import {
    ChangeDetectorRef,
    Component,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { LabelComponent } from "@components/label/label.component";
import { PageComponent } from "@components/page/page.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { ToggleListItemComponent } from "@components/toggle-list/toggle-list-item/toggle-list-item.component";
import { ToggleListComponent } from "@components/toggle-list/toggle-list.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Observable, of } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { CanBeDirty } from "~app/common/guards/dirty.guard";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { TypeaheadService } from "~app/common/services/typeahead/typeahead.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { OrderService } from "../../order/order.service";
import { DeleteLeverantorsorderCommand } from "../commands/DeleteLeverantorsorderCommand";
import { ILeverantorsorderDetail, ILeverantorsorderDetailRad } from "../models/leverantorsorder-detail-model";
import { LeverantorsorderService } from "../services/leverantorsorder.service";
import { CreateLeverantorsorderradModalComponent } from "./create-leverantorsorderrad-modal/create-leverantorsorderrad-modal.component";
import { LeverantorsorderHeaderComponent } from "./leverantorsorder-header/leverantorsorder-header.component";
import { LeverantorsorderLeveransraderComponent } from "./leverantorsorder-leveransrader/leverantorsorder-leveransrader.component";
import { LeverantorsorderradEditComponent } from "./leverantorsorderrad-edit/leverantorsorderrad-edit.component";

@Component({
    selector: "rask-leverantorsorder-details",
    templateUrl: "./leverantorsorder-details.component.html",
    styleUrls: ["./leverantorsorder-details.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        ActionButtonComponent,
        LeverantorsorderHeaderComponent,
        ToggleListComponent,
        LabelComponent,
        ToggleListItemComponent,
        LeverantorsorderradEditComponent,
        LeverantorsorderLeveransraderComponent,
        ButtonComponent,
        SaveButtonComponent,
        TranslatePipe
    ]
})
export class LeverantorsorderDetailsComponent implements OnInit, CanBeDirty {
    readonly router = inject(Router);
    readonly activatedRoute = inject(ActivatedRoute);
    readonly busyService = inject(BusyService);
    readonly typeaheadService = inject(TypeaheadService);
    readonly commandService = inject(CommandService);
    readonly toastService = inject(ToastService);
    readonly translateService = inject(TranslateService);
    readonly raskModalService = inject(RaskModalService);
    readonly modalService = inject(NgbModal);
    readonly cd = inject(ChangeDetectorRef);
    readonly orderService = inject(OrderService);
    readonly leverantorsorderService = inject(LeverantorsorderService);
    readonly authenticationService = inject(AuthenticationService);
    readonly dirtyFormService = inject(DirtyFormService);

    order: ILeverantorsorderDetail;
    rootForm: UntypedFormGroup;
    hookDeregisterFn: any;

    ngOnInit(): void {
        this.loadOrder();
        this.rootForm = new UntypedFormGroup({});
    }

    loadOrder() {
        const orderId = this.activatedRoute.snapshot.paramMap.get("id");
        this.busyService.addWork(this.leverantorsorderService
            .get(+orderId))
            .subscribe(res => {
                this.order = res;
            });
    }

    removeClick = (header, text, nextState) => {
        this.raskModalService.confirm(new ModalSettings(this.translateService.instant(header), this.translateService.instant(text)))
            .subscribe(result => {
                if (result === ConfirmDialogResponse.save) {
                    this.remove(nextState);
                }
            });
    };

    remove = (nextState) => {
        const currentCommands = this.commandService.transactionCommandQueue;
        this.commandService.reset();
        this.commandService.addTransactionCommand(new DeleteLeverantorsorderCommand(this.order.id));
        this.busyService
            .addWork(this.commandService.pushCommandQueueTransaction())
            // eslint-disable-next-line sonarjs/deprecation
            .subscribe(
                () => { // Success
                    this.router.navigate([nextState]);
                },
                () => { // Error
                    this.commandService.transactionCommandQueue = currentCommands;
                }
            );
    };

    removeOrderrad(levorderrad: ILeverantorsorderDetailRad) {
        const index = this.order.leverantorsorderrader.indexOf(levorderrad);
        this.order.leverantorsorderrader.splice(index, 1);
    }

    canEdit = () => this.authenticationService.canEdit();

    canSave() {
        return this.commandService.isDirty() && this.rootForm.valid;
    }

    undo = () => {
        this.commandService.reset();
        this.loadOrder();
    };

    save = async (): Promise<void> => {
        if (!this.canSave()) {
            return;
        }

        this.onBeforeSave().subscribe(confirmResult => {
            if (confirmResult === ConfirmDialogResponse.save) {
                const commands = this.commandService.transactionCommandQueue;
                this.busyService
                    .addWork(this.commandService.pushCommandQueueTransaction())
                    // eslint-disable-next-line sonarjs/deprecation
                    .subscribe(
                        () => {
                            this.toastService.add("Ändringarna sparades");
                            if (this.order.leverantorsorderrader.find(r => r.id === 0)) {
                                this.loadOrder();
                            }
                        },
                        () => {
                            this.commandService.transactionCommandQueue = commands;
                        });
            }
        });
    };

    addOrderrad = () => {
        this.raskModalService.open(this.translateService.instant("LEVERANTORSORDER_DETAIL_NYLEV"), CreateLeverantorsorderradModalComponent, "xl", true, {
            order: this.order,
            orderradAdded: this.orderradAdded,
            rootForm: this.rootForm
        });
    };

    orderradAdded = (orderrad) => {
        this.order.leverantorsorderrader.push(orderrad);
        this.cd.detectChanges();
    };

    readonly isDirtyFn = (): Promise<boolean> => new Promise(resolve => {
        if ((this.commandService.isDirty()) || this.canSave()) {
            const saveFn = !this.commandService.isDirty() && !this.canSave() ? (async () => {
                await this.save();
                resolve(true);
            }) : null;
            return this.dirtyFormService.dirtyFormModal(async () => {
                await this.commandService.reset();
                resolve(true);
            }, saveFn, (arg) => {
                resolve(false);
            });
        } else {
            resolve(true);
            return undefined;
        }
    });

    protected copy() {
        this.router.navigate(["copy"], { relativeTo: this.activatedRoute.parent });
    }

    private onBeforeSave(): Observable<string> {
        const numRows = this.levorderLeveransraderToBeDeleted();
        if (numRows === 0) {
            return of(ConfirmDialogResponse.save);
        }

        return this.raskModalService.confirm(new ModalSettings("Bekräfta",
            "Leverans- och försörjningsrader samt dess volymer tas bort i samband med att du sparar den nya tidsperioden"));
    }

    private levorderLeveransraderToBeDeleted(): number {
        let numRows = 0;
        this.order.leverantorsorderrader.forEach(levorderrad => {
            const fromDate = new Date(levorderrad.periodFrom);
            const toDate = new Date(levorderrad.periodTom);
            const count = levorderrad.levorderLeveransrader?.filter(
                x => x.kvantitet > 0 && (x.datum < fromDate || x.datum > toDate)
            ).length;

            numRows += ObjectUtils.isNullOrUndefined(count) ? 0 : count;
        });

        return numRows;
    }
}
